import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "cit-cookie-consent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      // Slight delay so it doesn't fight the initial page paint.
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const persist = (value) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ value, ts: new Date().toISOString() })
    );
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-2rem)] max-w-3xl"
    >
      <div className="card-flat shadow-2xl border-primary/20 bg-background/95 backdrop-blur-md p-5 sm:p-6 rounded-2xl">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm sm:text-[15px] tracking-tight">
              We value your privacy
            </div>
            <p className="mt-1.5 text-xs sm:text-[13px] text-muted-foreground leading-relaxed">
              We use cookies and browser storage to run the site, remember your
              theme preference, measure traffic and (with your consent) serve
              relevant ads through Google AdSense. See our{" "}
              <Link
                to="/legal/cookies"
                className="text-primary font-semibold hover:underline"
              >
                Cookie Policy
              </Link>{" "}
              and{" "}
              <Link
                to="/legal/privacy"
                className="text-primary font-semibold hover:underline"
              >
                Privacy Policy
              </Link>
              .
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                data-testid="cookie-accept"
                onClick={() => persist("accepted")}
                className="btn-primary h-9 px-4 text-xs font-semibold"
              >
                Accept all
              </Button>
              <Button
                data-testid="cookie-reject"
                variant="outline"
                onClick={() => persist("essential")}
                className="h-9 px-4 text-xs font-semibold"
              >
                Essential only
              </Button>
              <Link
                to="/legal/cookies"
                className="text-xs font-semibold text-muted-foreground hover:text-primary ml-1"
              >
                Learn more
              </Link>
            </div>
          </div>
          <button
            aria-label="Dismiss cookie notice"
            data-testid="cookie-dismiss"
            onClick={() => persist("dismissed")}
            className="shrink-0 w-8 h-8 rounded-full hover:bg-secondary/60 flex items-center justify-center text-muted-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
